import moment from 'moment';

import { getVariableValue, setVariableValue, isGlobalVariablePath, setGlobalVariable } from './globalVariableHelper';
import { mergeStyleRequest, updateVariableRequest, updateVariableSuccess } from '../store/page/actions';

import type { InternalFunctions } from './base';
import type { StyleUpdateChunk } from '../types/base';

// TNBEWEB-2022: the component an event belongs to — target of the visible `error` state when a
// `pre` function cancels that event.
type EventOwner = {
  // Node id, i.e. the `object_id` of a style chunk targeting this component.
  id: string;
  // The same row arguments the component passes to its own style chunks.
  parentIndex?: number;
  row?: any;
  // The active_state the component rendered with (unknown on the saga path).
  activeState?: unknown;
};

// Components the renderer itself put into `error` because a `pre` function cancelled their
// event. Only these are cleared on the next pass — an `error` the author set is never touched.
const cancelledByPre = new Set<string>();

const setOwnerState = (store: BuildArgs['store'], owner: EventOwner, activeState: StyleUpdateChunk['active_state']) => {
  // Exactly the chunk an author would write, so bound (`{{v.*}}`) states and row scoping behave
  // as they do for author style chunks. `object_type` only disambiguates templated object_ids
  // (matchesPattern); an exact node id matches on its own.
  store.dispatch(mergeStyleRequest(
    [{
      object_id: owner.id,
      object_type: '',
      active_state: activeState,
      style: {},
    }],
    owner.parentIndex,
    owner.row,
  ));
};

type OwnerHooks = Pick<InternalFunctions, 'onPreCancelled' | 'onPrePassed'>;

const buildOwnerHooks = (store: BuildArgs['store'], owner: EventOwner): OwnerHooks => ({
  onPreCancelled: () => {
    cancelledByPre.add(owner.id);
    setOwnerState(store, owner, 'error');
  },
  onPrePassed: () => {
    if (!cancelledByPre.delete(owner.id)) return;
    // Something else replaced our `error` since (e.g. `disabled`): leave it.
    if (owner.activeState === undefined || owner.activeState === 'error') {
      setOwnerState(store, owner, 'base');
    }
  },
});

// Nest a dotted path into an object: nest('a.b.c', v) => { a: { b: { c: v } } }.
// Mirrors the updateVariableRequest saga (sagas.ts) and the saga response path so the
// reducer receives the same deep-write shape.
const nest = (dotted: string, value: any): any =>
  dotted.split('.').reduceRight<any>((acc, k) => ({ [k]: acc }), value);

type BuildArgs = {
  // Redux store (from `useStore()` in components, or the store singleton in sagas).
  store: { getState: () => any; dispatch: (action: any) => any };
  // Current repeatable row object + its write path, for the handler's `this` (TNBEWEB-1992).
  // Both undefined outside a repeatable row.
  thisRow?: any;
  rowPath?: string;
  // Backend response context, for the saga on_success/on_error handlers.
  response?: any;
  // Saga response path only: make legacy $set/$setVariable dispatch UPDATE_VARIABLE_SUCCESS
  // directly (synchronous) instead of UPDATE_VARIABLE_REQUEST. Preserves the pre-existing saga
  // behaviour where a later function-chunk in the same on_success can $get an earlier chunk's
  // $set. Components keep the UPDATE_VARIABLE_REQUEST path (unchanged).
  directWrites?: boolean;
  // TNBEWEB-2022: the component whose event these functions run for (see EventOwner).
  owner?: EventOwner;
};

// Single source of truth for the JS event-handler `internalFns` scope. Replaces the 10
// identical inline copies across the screen components + the saga response path.
//
// On top of the legacy `$get`/`$set`/`$getVariable`/`$setVariable`/`$moment` scope it adds
// `writeVar` (the write path behind data.* / this.* / _c.* — see base.ts:buildHandlerScope)
// and the row context (`thisRow` / `rowPath`) consumed by executeFunction's `this` binding.
// eslint-disable-next-line import/prefer-default-export
export const buildInternalFns = ({ store, thisRow, rowPath, response, directWrites, owner }: BuildArgs): InternalFunctions => {
  // Write path for data.* / this.* (local) and _c.*/_config.* (global). Matches the ticket:
  // dispatch UPDATE_VARIABLE_SUCCESS for local variables, write localStorage for globals. The
  // direct SUCCESS dispatch (the same pattern the saga response path and applyVariablesFirst
  // already use) commits synchronously — so read-after-write in the same handler works — and
  // cannot be dropped by the `takeLatest` guard on UPDATE_VARIABLE_REQUEST that $set rides.
  const writeVar = (path: string, value: any) => {
    if (isGlobalVariablePath(path)) {
      setGlobalVariable(path, value);
    } else {
      store.dispatch(updateVariableSuccess({ data: nest(path, value), replaceArrays: true }));
    }
  };

  return {
    // --- legacy scope: byte-for-byte with the previous inline objects for each context ---
    $setVariable: directWrites
      ? (key: string, value: any) => setVariableValue(key, value, (k, v) => writeVar(k, v))
      : (key: string, value: any) => setVariableValue(key, value, (k, v) => {
        store.dispatch(updateVariableRequest({ variable: k, value: v }));
      }),
    $getVariable: (key: string) => {
      // state.page.variableData / settings.variables are the selectVariableData /
      // selectLocalVariableData selectors inlined (avoids a page/selectors -> store -> sagas
      // -> handlerScope import cycle); identical read to the previous inline internalFns.
      const state = store.getState();
      return getVariableValue(key, state.page?.variableData, state.page?.settings?.variables);
    },
    $moment: (...args: any[]) => moment(...args),
    ...(response !== undefined && { $response: response }),

    // --- NEW write path behind data.* / _c.* / _config.* / this.* ---
    writeVar,

    // --- NEW row context for `this` (both undefined outside a repeatable row) ---
    thisRow,
    rowPath,

    // --- TNBEWEB-2022: a cancelled `pre` puts the owner into `error`; the next pass clears it ---
    ...(owner?.id ? buildOwnerHooks(store, owner) : {}),
  };
};
